import { useEffect, useRef, useState } from "react";
import { api, apiError } from "@/lib/apiClient";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { PodcastAdmin } from "@/components/PodcastAdmin";
import { PremioMinimoBox } from "@/components/PremioMinimoBox";
import { Package, Upload, CheckCircle2, Circle, FileText, Plus, Loader2, Sparkles } from "lucide-react";

const slugify = (s) => (s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "")
  .replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "").slice(0, 40);

const brochureUrl = (code) => `${process.env.REACT_APP_BACKEND_URL}/api/public/products/${code}/brochure`;

const DOC_SLOTS = [
  ["cga_file", "Condizioni Generali (CGA)", true],
  ["dip_file", "DIP", true],
  ["dip_agg_file", "DIP aggiuntivo", true],
  ["set_informativo_file", "Set informativo", false],
  ["norme_tariffarie_file", "Norme tariffarie", false],
  ["norme_operative_file", "Norme operative", false],
];

export default function ProductFactory() {
  const [products, setProducts] = useState([]);
  const [uploading, setUploading] = useState("");
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [np, setNp] = useState({ name: "", code: "", clusters: "" });
  const [schedaBusy, setSchedaBusy] = useState("");
  const refs = useRef({});

  const load = () => api.get("/products").then(({ data }) => setProducts(data));
  useEffect(() => { load(); }, []);

  const createProduct = async () => {
    const name = np.name.trim();
    if (!name) { toast.error("Inserisci il nome del prodotto"); return; }
    const code = (np.code.trim() || slugify(name));
    setCreating(true);
    try {
      await api.post("/admin/products", {
        code, name,
        clusters: np.clusters.split(",").map((c) => c.trim()).filter(Boolean),
        attivo: true,
      });
      toast.success("Prodotto creato — ora puoi caricare i documenti");
      setOpen(false);
      setNp({ name: "", code: "", clusters: "" });
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setCreating(false); }
  };

  const upload = async (code, slot, file) => {
    if (!file) return;
    setUploading(`${code}-${slot}`);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("tipo", slot);
      const { data } = await api.post("/upload", fd);
      const fd2 = new FormData();
      fd2.append("slot", slot);
      fd2.append("file_id", data.storage_path);
      await api.post(`/admin/products/${code}/documenti`, fd2);
      toast.success("Documento caricato");
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setUploading(""); }
  };

  const rigeneraScheda = async (code) => {
    setSchedaBusy(code);
    try {
      const { data } = await api.post(`/admin/products/${code}/scheda-ai/rigenera`);
      toast[data.disponibile ? "success" : "error"](
        data.disponibile ? "Scheda intelligente rigenerata" : "Nessun documento indicizzato: carica prima CGA/DIP");
    } catch (e) { toast.error(apiError(e)); }
    finally { setSchedaBusy(""); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-semibold text-forest">Product Factory</h1>
          <p className="mt-1 text-muted-foreground">Catalogo prodotti e documenti precontrattuali versionati.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-product-button" className="gap-1.5 bg-terracotta hover:bg-terracotta-hover">
              <Plus className="h-4 w-4" /> Nuovo prodotto
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="new-product-dialog">
            <DialogHeader><DialogTitle>Nuovo prodotto assicurativo</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2">
              <div>
                <Label>Nome prodotto</Label>
                <Input data-testid="np-name" value={np.name} placeholder="Es. Tutela Legale ETS"
                  onChange={(e) => setNp({ ...np, name: e.target.value })} className="mt-1" />
              </div>
              <div>
                <Label>Codice (facoltativo)</Label>
                <Input data-testid="np-code" value={np.code} placeholder={np.name ? slugify(np.name) : "generato dal nome"}
                  onChange={(e) => setNp({ ...np, code: e.target.value })} className="mt-1" />
              </div>
              <div>
                <Label>Cluster (separati da virgola, facoltativo)</Label>
                <Input data-testid="np-clusters" value={np.clusters} placeholder="ODV / APS, Cooperativa sociale"
                  onChange={(e) => setNp({ ...np, clusters: e.target.value })} className="mt-1" />
              </div>
              <p className="text-xs text-muted-foreground">
                Prodotto informativo/documentale: dopo la creazione carica Set Informativo, DIP, DIP Aggiuntivo e CGA.
                Comparirà anche in Assistente tecnico e Framework relazioni.
              </p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
              <Button data-testid="np-submit" onClick={createProduct} disabled={creating} className="gap-1.5 bg-forest hover:bg-forest/90">
                {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Crea prodotto
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {products.map((p) => (
        <Card key={p.code} data-testid={`product-${p.code}`} className="rounded-xl border-border/60 p-6">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-sage text-forest"><Package className="h-6 w-6" /></div>
              <div>
                <h2 className="font-heading text-xl font-semibold text-forest">{p.name}</h2>
                <div className="mt-0.5 flex items-center gap-2 text-xs text-muted-foreground">
                  <Badge variant="outline" className="border-forest/20 text-forest">Ed. {p.versione}</Badge>
                  <span>motore: {p.engine_ref}</span>
                </div>
              </div>
            </div>
            <div className="flex flex-col items-end gap-2">
              {DOC_SLOTS.filter(([, , req]) => req).every(([slot]) => !!p.documenti?.[slot]) ? (
                <Badge data-testid={`doc-status-${p.code}`} className="border-emerald-200 bg-emerald-100 text-emerald-700">Documentazione cliente completa</Badge>
              ) : (
                <Badge data-testid={`doc-status-${p.code}`} className="border-amber-300 bg-amber-100 text-amber-700">Set informativo incompleto</Badge>
              )}
              <Button size="sm" variant="outline" onClick={() => rigeneraScheda(p.code)} disabled={schedaBusy === p.code}
                data-testid={`rigenera-scheda-${p.code}`} className="h-7 gap-1.5 text-xs text-forest">
                {schedaBusy === p.code ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />} Rigenera Scheda AI
              </Button>
            </div>
          </div>

          <PremioMinimoBox product={p} />

          {p.clusters?.length > 0 && (
            <div className="mt-4">
              <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Cluster</span>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {p.clusters.map((c) => <Badge key={c} variant="secondary" className="bg-sage text-forest">{c}</Badge>)}
              </div>
            </div>
          )}

          <div className="mt-5">
            <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Documenti precontrattuali</span>
            <div className="mt-2 grid gap-2 sm:grid-cols-2">
              {DOC_SLOTS.map(([slot, label, required]) => {
                const has = !!p.documenti?.[slot];
                return (
                  <div key={slot} className="flex items-center justify-between rounded-lg border border-border/60 px-3 py-2.5">
                    <div className="flex items-center gap-2">
                      {has ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <Circle className="h-4 w-4 text-muted-foreground/40" />}
                      <span className="text-sm text-forest">{label}</span>
                      {required && !has && <span className="text-[10px] font-medium text-amber-600">obbligatorio</span>}
                    </div>
                    <input ref={(el) => (refs.current[`${p.code}-${slot}`] = el)} type="file" accept="application/pdf" className="hidden"
                      onChange={(e) => upload(p.code, slot, e.target.files[0])} />
                    <Button size="sm" variant="ghost" onClick={() => refs.current[`${p.code}-${slot}`]?.click()}
                      data-testid={`upload-${slot}`} disabled={uploading === `${p.code}-${slot}`} className="h-7 gap-1 text-xs text-forest">
                      {has ? <FileText className="h-3.5 w-3.5" /> : <Upload className="h-3.5 w-3.5" />} {has ? "Sostituisci" : "Carica"}
                    </Button>
                  </div>
                );
              })}
            </div>
            <div className="mt-4 rounded-lg border border-border/60 bg-[#F5F7FB] p-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-start gap-2">
                  {p.documenti?.brochure_file
                    ? <CheckCircle2 className="mt-0.5 h-4 w-4 text-emerald-600" />
                    : <Circle className="mt-0.5 h-4 w-4 text-muted-foreground/40" />}
                  <div>
                    <span className="text-sm font-medium text-forest">Brochure commerciale (PDF)</span>
                    <p className="max-w-lg text-xs text-muted-foreground">
                      Carica un PDF preparato con strumenti esterni: sarà scaricabile da Partner e Cliente dal link.
                      Se non caricato, la piattaforma genera automaticamente una brochure informativa.
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <a href={brochureUrl(p.code)} target="_blank" rel="noopener noreferrer"
                    data-testid={`brochure-preview-${p.code}`}
                    className="inline-flex items-center gap-1 rounded-md border border-border px-3 py-1.5 text-xs font-medium text-forest transition-colors hover:bg-sage">
                    <FileText className="h-3.5 w-3.5" /> Anteprima
                  </a>
                  <input ref={(el) => (refs.current[`${p.code}-brochure_file`] = el)} type="file" accept="application/pdf" className="hidden"
                    onChange={(e) => upload(p.code, "brochure_file", e.target.files[0])} />
                  <Button size="sm" variant="ghost" onClick={() => refs.current[`${p.code}-brochure_file`]?.click()}
                    data-testid={`upload-brochure-${p.code}`} disabled={uploading === `${p.code}-brochure_file`}
                    className="h-7 gap-1 text-xs text-forest">
                    {uploading === `${p.code}-brochure_file`
                      ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      : <Upload className="h-3.5 w-3.5" />}
                    {p.documenti?.brochure_file ? "Sostituisci" : "Carica"}
                  </Button>
                </div>
              </div>
            </div>
            <p className="mt-3 text-xs text-muted-foreground">
              I documenti obbligatori (CGA, DIP, DIP agg.) sono necessari per completare il bundle IVASS ed abilitare l'emissione.
            </p>
            <PodcastAdmin code={p.code} />
          </div>
        </Card>
      ))}
    </div>
  );
}
